export type SignCampaignEventType =
  | 'scan_landed'
  | 'free_mode_started'
  | 'first_interaction'
  | 'searched_facility'
  | 'started_navigation'
  | 'opened_jsa'
  | 'opened_hazards'
  | 'opened_ar';

type SignCampaignAttribution = {
  scanSessionId: string;
  source?: string;
  sign?: string;
  campaign?: string;
  creative?: string;
  placement?: string;
  language?: string;
  expiresAt: number;
};

const ATTRIBUTION_STORAGE_KEY = 'zia_sign_campaign_attribution_v1';
const SENT_EVENTS_STORAGE_KEY = 'zia_sign_campaign_sent_events_v1';
const ATTRIBUTION_TTL_MS = 30 * 60 * 1000;
const EVENT_ENDPOINT = '/api/analytics/sign-conversion';

const cleanParam = (value: string | null, maxLength: number): string | undefined => {
  if (!value) return undefined;
  const cleaned = value.toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, maxLength);
  return cleaned || undefined;
};

const readJson = <T,>(key: string): T | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) as T : null;
  } catch {
    return null;
  }
};

const writeJson = (key: string, value: unknown): void => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // QR analytics should never block the app if browser storage is unavailable.
  }
};

export const getActiveSignCampaignAttribution = (): SignCampaignAttribution | null => {
  const attribution = readJson<SignCampaignAttribution>(ATTRIBUTION_STORAGE_KEY);
  if (!attribution?.scanSessionId || attribution.expiresAt <= Date.now()) {
    try { window.sessionStorage.removeItem(ATTRIBUTION_STORAGE_KEY); } catch { /* noop */ }
    return null;
  }
  return attribution;
};

export const captureSignCampaignFromUrl = (params: URLSearchParams): SignCampaignAttribution | null => {
  const scanSessionId = cleanParam(params.get('scan'), 80);
  if (!scanSessionId) return null;

  const language = cleanParam(params.get('lang') ?? params.get('language'), 16);
  const attribution: SignCampaignAttribution = {
    scanSessionId,
    source: cleanParam(params.get('src'), 64),
    sign: cleanParam(params.get('sign') ?? params.get('s'), 64),
    campaign: cleanParam(params.get('campaign') ?? params.get('c'), 64),
    creative: cleanParam(params.get('creative') ?? params.get('cr'), 64),
    placement: cleanParam(params.get('placement') ?? params.get('pl'), 64),
    language,
    expiresAt: Date.now() + ATTRIBUTION_TTL_MS,
  };

  writeJson(ATTRIBUTION_STORAGE_KEY, attribution);
  writeJson(SENT_EVENTS_STORAGE_KEY, []);
  return attribution;
};

const getSentEventKeys = (): Set<string> => {
  const keys = readJson<string[]>(SENT_EVENTS_STORAGE_KEY);
  return new Set(Array.isArray(keys) ? keys : []);
};

const markEventSent = (attribution: SignCampaignAttribution, eventType: SignCampaignEventType): boolean => {
  const key = `${attribution.scanSessionId}:${eventType}`;
  const keys = getSentEventKeys();
  if (keys.has(key)) return false;
  keys.add(key);
  writeJson(SENT_EVENTS_STORAGE_KEY, Array.from(keys));
  return true;
};

export const recordSignCampaignEvent = (
  eventType: SignCampaignEventType,
  metadata?: Record<string, string | number | boolean | null | undefined>,
): boolean => {
  const attribution = getActiveSignCampaignAttribution();
  if (!attribution || !markEventSent(attribution, eventType)) return false;

  const payload = {
    scanSessionId: attribution.scanSessionId,
    eventType,
    timestamp: new Date().toISOString(),
    source: attribution.source,
    sign: attribution.sign,
    campaign: attribution.campaign,
    creative: attribution.creative,
    placement: attribution.placement,
    language: attribution.language,
    metadata,
  };
  const body = JSON.stringify(payload);

  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon(EVENT_ENDPOINT, blob)) return true;
    }
  } catch {
    // Fall through to fetch.
  }

  try {
    void fetch(EVENT_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      credentials: 'same-origin',
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // Ignore analytics send failures.
  }
  return true;
};

let firstInteractionInstalled = false;

export const installSignCampaignFirstInteractionTracker = (): void => {
  if (firstInteractionInstalled || typeof window === 'undefined' || !getActiveSignCampaignAttribution()) return;
  firstInteractionInstalled = true;

  let handler: () => void;
  const cleanup = () => {
    window.removeEventListener('pointerdown', handler, true);
    window.removeEventListener('touchstart', handler, true);
    window.removeEventListener('keydown', handler, true);
    firstInteractionInstalled = false;
  };
  handler = () => {
    recordSignCampaignEvent('first_interaction');
    cleanup();
  };

  window.addEventListener('pointerdown', handler, true);
  window.addEventListener('touchstart', handler, true);
  window.addEventListener('keydown', handler, true);
};
